import { makeStyles, Spinner, Text, tokens } from "@fluentui/react-components";
import { useTranslation } from "react-i18next";
import { SearchResult } from "../../hooks/useTagSearch";
import { SkanskaColorTokens } from "../../style/colors";

interface TagSearchResultsProps {
  results: SearchResult[];
  searchTerm: string;
  isLoading: boolean;
  onSelectResult: (result: SearchResult) => void;
}

const useStyles = makeStyles({
  container: {
    overflow: "auto",
    flex: 1,
    borderTop: `1px solid ${SkanskaColorTokens.color_secondary_middle_gray}`,
  },
  noResults: {
    padding: tokens.spacingVerticalM,
    color: SkanskaColorTokens.color_secondary_dark_gray,
  },
  collectionGroup: {
    marginBottom: tokens.spacingVerticalL,
  },
  collectionTitle: {
    fontWeight: "600",
    padding: `${tokens.spacingVerticalXS} ${tokens.spacingHorizontalM}`,
    color: SkanskaColorTokens.color_primary_blue,
    borderBottom: `1px solid ${SkanskaColorTokens.color_secondary_light_gray}`,
  },
  resultItem: {
    display: "flex",
    flexDirection: "column",
    padding: `${tokens.spacingVerticalXS} ${tokens.spacingHorizontalM}`,
    cursor: "pointer",
    borderRadius: tokens.borderRadiusMedium,
    ":hover": {
      backgroundColor: SkanskaColorTokens.color_primary_gray,
    },
  },
  titleRow: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    whiteSpace: "pre-wrap",
  },
  highlight: {
    fontWeight: "700",
    color: SkanskaColorTokens.color_primary_blue,
  },
  pathText: {
    fontSize: tokens.fontSizeBase200,
    color: SkanskaColorTokens.color_secondary_dark_gray,
  },
});

export const TagSearchResults = (props: TagSearchResultsProps) => {
  const { results, searchTerm, isLoading, onSelectResult } = props;
  const classes = useStyles();
  const { t } = useTranslation();

  if (isLoading) {
    return <Spinner />;
  }

  if (results.length === 0) {
    return <div className={classes.noResults}>{t("noMatchingTags")}</div>;
  }

  const groups = results.reduce<Record<string, SearchResult[]>>(
    (acc, result) => {
      if (!acc[result.collectionTitle]) {
        acc[result.collectionTitle] = [];
      }
      acc[result.collectionTitle].push(result);
      return acc;
    },
    {}
  );

  return (
    <div className={classes.container}>
      {Object.entries(groups).map(([collectionTitle, groupResults]) => (
        <div key={collectionTitle} className={classes.collectionGroup}>
          <div className={classes.collectionTitle}>{collectionTitle}</div>
          {groupResults.map((result) => {
            const matchIndex = result.title
              .toLowerCase()
              .indexOf(searchTerm.toLowerCase());
            const before = result.title.substring(0, matchIndex);
            const match = result.title.substring(
              matchIndex,
              matchIndex + searchTerm.length
            );
            const after = result.title.substring(
              matchIndex + searchTerm.length
            );
            return (
              <div
                key={result.id}
                className={classes.resultItem}
                onClick={() => onSelectResult(result)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ")
                    onSelectResult(result);
                }}
              >
                <div className={classes.titleRow}>
                  <span>{before}</span>
                  <span className={classes.highlight}>{match}</span>
                  <span>{after}</span>
                </div>
                <Text className={classes.pathText}>{result.displayPath}</Text>
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
};
